import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { ComponentSkinProps } from '../../../../types/component-skin';
import { PriceDisplay } from '../../../common/PriceDisplay';
import SafeImage from '../../../common/SafeImage';
import '../ProductDetail.css';

/**
 * 상품 상세 기본 스킨
 */
const BasicProductDetailSkin: React.FC<ComponentSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils 
}) => {
  const { t } = utils;
  
  // 데이터 추출
  const { 
    product,
    selectedOptions = {},
    quantity = 1,
    loading = false,
    error,
    activeTab = 'description',
    mainImage,
    allImages = [],
    relatedProducts = [],
    selectedVariant,
    isMobile = false,
    showBottomSheet = false,
    isUserLoggedIn = false,
    isAdminMode = false,
    theme = {},
    basePrice = 0,
    priceInfo,
    finalPrice = 0,
    isOutOfStock = false
  } = data || {};
  
  // 액션 추출
  const {
    handleOptionChange,
    handleQuantityChange,
    handleAddToCart,
    handleBuyNow,
    handleTabChange,
    handleImageChange,
    increaseQuantity,
    decreaseQuantity,
    setShowBottomSheet
  } = actions || {};
  
  // 옵션 추출 - componentProps와 options 병합
  const componentProps = data?.componentProps || {};
  const mergedOptions = { ...options, ...componentProps };
  
  const {
    showDescriptionTab = true,
    showReviewsTab = true,
    showSpecificationTab = false,
    showStock = false,
    order = "",
    none = "none",
    lg = 9,
    showRewardPoints = false,
    rewardPointBase = 'pv',
    rewardPointPercentage = 1,
    rewardPointLabel = '적립금',
    rewardPointFormat = 'amount'
  } = mergedOptions;
  
  // 별점 컴포넌트
  const StarRating: React.FC<{ rating?: number }> = ({ rating = 4 }) => {
    const safeRating = typeof rating === 'number' && !isNaN(rating) 
      ? Math.min(Math.max(0, rating), 5) 
      : 4;
    
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= safeRating) {
        stars.push(<i key={i} className="gicon gi-star fill"></i>);
      } else {
        stars.push(<i key={i} className="gicon gi-star-o"></i>);
      }
    }
    return <>{stars}</>;
  };

  // 수량 선택 컴포넌트
  const QuantitySelector: React.FC = () => {
    return (
      <div className="quantity-selector">
        {!isMobile && <span className="quantity-label"></span>}
        <button className="qty-down" onClick={decreaseQuantity}>-</button>
        <input className="qty-input" type="number" value={quantity} readOnly />
        <button className="qty-up" onClick={increaseQuantity}>+</button>
      </div>
    );
  };
  
  // 가격 포맷팅 함수
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원';
  };
  
  // 적립금 계산 함수
  const calculateRewardPoints = () => {
    if (!showRewardPoints || !product) return null;
    
    // PV 기준일 때 PV 값이 없으면 표시하지 않음
    if (rewardPointBase === 'pv' && (!product.pv || product.pv === 0)) {
      return null;
    }
    
    // variant가 선택되어 추가 금액이 있는 경우, finalPrice(합한 금액) 사용
    // PV 기준이면 PV 값 사용, 판매가 기준이면 finalPrice 사용
    const baseValue = rewardPointBase === 'pv' ? product.pv : finalPrice;
    const points = Math.floor(baseValue * (rewardPointPercentage / 100));
    
    if (points === 0) return null;
    
    switch (rewardPointFormat) {
      case 'percentage':
        return `${rewardPointPercentage}%`;
      case 'both':
        return `${formatPrice(points)} (${rewardPointPercentage}%)`;
      case 'amount':
      default:
        return formatPrice(points);
    }
  };
  
  // 마크다운 형식의 이미지 URL 추출
  const extractImagesFromMarkdown = (markdown?: string): string[] => {
    if (!markdown) return [];
    const regex = /!\[.*?\]\((.*?)\)/g;
    const matches: string[] = [];
    let match;
    
    while ((match = regex.exec(markdown)) !== null) {
      matches.push(match[1]);
    }
    
    return matches;
  };
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="product-loading">
        <p>{t('상품 정보를 불러오는 중입니다...')}</p>
      </div>
    );
  }
  
  // 오류 발생 시 표시
  if (error && !product) {
    const errorMessage = typeof error === 'string' ? error : '상품 정보를 불러오는데 실패했습니다.';
    return (
      <div className="product-error">
        <p>{errorMessage}</p>
      </div>
    );
  }
  
  // 데이터가 없는 경우
  if (!product) {
    return (
      <div className="no-product-data">
        <p>{t('상품 정보를 찾을 수 없습니다.')}</p>
      </div>
    );
  }
  
  // 설명에서 이미지 추출
  const descriptionImages = extractImagesFromMarkdown(product?.description);
  
  // 보이는 탭 중 첫 번째 탭을 기본값으로 설정
  const getVisibleTabs = () => {
    const tabs = [];
    if (showDescriptionTab) tabs.push('description');
    if (showReviewsTab) tabs.push('reviews');
    if (showSpecificationTab) tabs.push('specification');
    return tabs;
  };
  
  const visibleTabs = getVisibleTabs();
  const currentActiveTab = visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0];

  return (
    <section className="product-detail-container">
      <div className={isMobile ? "" : "container-fluid"}>
        <Row className="justify-content-center">
          <Col
            lg={7}
            md={12}
            className={`gi-pro-rightside gi-common-rightside ${order}`}
            style={{maxWidth: "100%", padding: isMobile ? "0" : "0 15px"}}
          >
            {/* Single product content Start */}
            <div className="single-pro-block">
              <div className="single-pro-inner">
                <Row>
                  <Col sm={12} md={6} className="single-pro-img">
                    <div className="single-product-scroll">
                      {/* 메인 이미지 직접 표시 */}
                      <div className="main-product-image">
                        {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                        <SafeImage 
                          key={`main-image-${product?.id}-${mainImage}`}
                          src={mainImage} 
                          alt={product?.title || product?.name || t('상품명')} 
                          style={{
                            width: "100%", 
                            height: "auto", 
                            borderRadius: isMobile ? "0" : "5px", 
                            marginBottom: isMobile ? "10px" : "15px"
                          }} 
                          placeholderStyle={{
                            width: "100%",
                            paddingTop: "75%", // 4:3 비율
                            backgroundColor: "#cccccc",
                            borderRadius: isMobile ? "0" : "5px",
                            marginBottom: isMobile ? "10px" : "15px",
                            position: "relative"
                          }}
                        />
                      </div>
                      
                      {/* 추가 이미지 썸네일 */}
                      {allImages.length > 1 && (
                        <div className="product-image-thumbnails">
                          {allImages.map((imgUrl: string, index: number) => (
                            <div 
                              key={`${product?.id}-${index}-${imgUrl}`} 
                              className={`thumbnail-item ${mainImage === imgUrl ? 'active' : ''}`}
                              onClick={() => handleImageChange(imgUrl)}
                              onMouseEnter={() => handleImageChange(imgUrl)}
                            >
                              {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                              <SafeImage 
                                key={`thumb-${product?.id}-${index}-${imgUrl}`}
                                src={imgUrl} 
                                alt={`${product?.title || t('상품명')} ${index + 1}`} 
                                style={{width: "100%", height: "100%", objectFit: "cover"}} 
                                placeholderStyle={{
                                  width: "100%",
                                  height: "100%",
                                  backgroundColor: "#cccccc"
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={12} md={6} className="single-pro-desc">
                    <div className="single-pro-content">
                      <h5 className="gi-single-title">
                        {t(product?.title as string) || t(product?.name as string) || t('상품명')}
                      </h5>
                      {(product?.averageRating || product?.totalReviews > 0) && (
                        <div className="gi-single-rating-wrap">
                          <div className="gi-single-rating">
                            <StarRating rating={product?.averageRating} />
                          </div>
                          {product?.totalReviews > 0 && (
                            <span className="review-count">({product.totalReviews})</span>
                          )}
                        </div>
                      )}

                      <div className="gi-single-price-stoke">
                        <div className="gi-single-price">
                          {priceInfo && priceInfo.discount > 0 && priceInfo.levelName ? (
                            <>
                              {/* 등급 특별가 라벨 */}
                              <div className="level-price-label">
                                <span className="level-badge">{priceInfo.levelName} 특별가</span>
                                <span className="discount-rate">-{priceInfo.discountRate}%</span>
                              </div>
                              
                              {/* 가격 표시 */}
                              <div className="price-display">
                                <span className="original-price">
                                  {/* @ts-ignore */}
                                  <PriceDisplay price={priceInfo.originalPrice} />
                                </span>
                                <span className="level-price">
                                  {/* @ts-ignore */}
                                  <PriceDisplay price={priceInfo.levelPrice} />
                                </span>
                              </div>
                              
                              {/* 절약 금액 */}
                              <div className="saved-amount">
                                {priceInfo.discount.toLocaleString()}원 절약
                              </div>
                            </>
                          ) : (
                            // 등급 할인이 없는 경우
                            <div className="final-price">
                              {/* @ts-ignore */}
                              <PriceDisplay price={basePrice} />
                            </div>
                          )}
                        </div>
                        {showRewardPoints && calculateRewardPoints() && (
                          <div className="gi-reward-points" style={{ marginTop: '10px', fontSize: '14px', color: '#ff9800' }}>
                            {rewardPointLabel}: {calculateRewardPoints()}
                          </div>
                        )}
                        {showStock && (
                          <div className="gi-single-stoke">
                            <span className="gi-single-sku">{t("상품코드")}: {product?.sku || product?.id || ""}</span>
                            {product?.config?.stock_count === 0 && (
                              <span className="gi-single-ps-title out-of-stock">
                                {t("품절")}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* 태그 정보 있으면 표시 */}
                      {(product?.tags) && (product.tags).trim() !== "" && (
                        <div className="gi-single-tags">
                          <strong>{t("태그")}:</strong> {product.tags}
                        </div>
                      )}

                      {product?.features && product.features.length > 0 && (
                        <div className="gi-single-list">
                          <ul>
                            {product.features.map((feature: any, index: number) => (
                              <li key={index}>
                                <strong>{feature.label} :</strong> {feature.value}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {product?.weights && product.weights.length > 0 && (
                        <div className="gi-pro-variation">
                          <div className="gi-pro-variation-inner gi-pro-variation-size">
                            <span>{t("무게")}</span>
                            <div className="gi-pro-variation-content">
                              <ul>
                                {product.weights.map((weight: any, index: number) => (
                                  <li key={index} className={index === 0 ? "active" : ""}>
                                    <span>{weight}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 옵션 선택 UI - PC에서만 여기에 표시 */}
                      {!isMobile && product?.variants && product.variants.length > 0 && 
                       product?.optionGroups && product.optionGroups.length > 0 && (
                        <div className="gi-product-options">
                          {product.optionGroups.map((group: any) => (
                            <div key={group.id} className="gi-option-group">
                              <div className="gi-option-label">
                                <span>{group.name}</span>
                                {group.isRequired && <span className="required">*</span>}
                              </div>
                              <div className="gi-option-values">
                                {group.optionValues?.map((value: any) => (
                                  <button
                                    key={value.id}
                                    className={`gi-option-button ${selectedOptions[group.id] === value.id ? 'active' : ''}`}
                                    onClick={() => handleOptionChange(group.id, value.id)}
                                    disabled={!value.isActive}
                                  >
                                    {value.value}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* PC에서만 선택된 variant 정보 표시 */}
                      {!isMobile && product?.variants && product.variants.length > 0 && selectedVariant && (
                        <div className="gi-variant-info">
                          <div className="gi-variant-sku">
                            {t("선택된 상품")}: {selectedVariant.sku}
                          </div>
                          {Number(selectedVariant.additionalPrice) > 0 && (
                            <div className="gi-variant-price">
                              <span>{t("추가 금액")}: </span>
                              <span className="additional-price-amount">
                                +₩{Number(selectedVariant.additionalPrice).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {selectedVariant.stock === 0 && (
                            <div className="gi-variant-stock out-of-stock">
                              {t("품절")}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* 모바일에서 하단 버튼 */}
                      {isMobile && !showBottomSheet && (
                        <div className="mobile-bottom-button">
                          <button 
                            className="show-purchase-button"
                            onClick={() => setShowBottomSheet(true)}
                          >
                            <span>{t('장바구니 추가')}</span>
                            <span className="price">
                              {/* @ts-ignore */}
                              <PriceDisplay price={finalPrice * quantity} />
                            </span>
                          </button>
                        </div>
                      )}
                      
                      {/* 모바일 오버레이 */}
                      {isMobile && showBottomSheet && (
                        <div className="mobile-overlay" onClick={() => setShowBottomSheet(false)} />
                      )}
                      
                      <div className={`gi-single-qty ${isMobile && product?.variants && product.variants.length > 0 && product?.optionGroups && product.optionGroups.length > 0 ? 'with-options' : ''} ${isMobile && !showBottomSheet ? 'hidden' : ''}`}>
                        {/* 모바일에서 닫기 핸들 */}
                        {isMobile && showBottomSheet && (
                          <div className="bottom-sheet-handle" onClick={() => setShowBottomSheet(false)}>
                            <span className="handle-bar"></span>
                          </div>
                        )}
                        
                        {/* 모바일에서 옵션 선택 */}
                        {isMobile && product?.variants && product.variants.length > 0 && 
                         product?.optionGroups && product.optionGroups.length > 0 && (
                          <div className="bottom-options">
                            {product.optionGroups.map((group: any) => (
                              <div key={group.id} className="gi-option-group">
                                <div className="gi-option-label">
                                  <span>{group.name}</span>
                                  {group.isRequired && <span className="required">*</span>}
                                </div>
                                <div className="gi-option-values">
                                  {group.optionValues?.map((value: any) => (
                                    <button
                                      key={value.id}
                                      className={`gi-option-button ${selectedOptions[group.id] === value.id ? 'active' : ''}`}
                                      onClick={() => handleOptionChange(group.id, value.id)}
                                      disabled={!value.isActive}
                                    >
                                      {value.value}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                            
                            {/* 모바일에서 선택된 variant 정보 표시 */}
                            {selectedVariant && (
                              <div className="gi-variant-info mobile">
                                <div className="gi-variant-sku">
                                  {t("선택된 상품")}: {selectedVariant.sku}
                                </div>
                                {Number(selectedVariant.additionalPrice) > 0 && (
                                  <div className="gi-variant-price">
                                    <span>{t("추가 금액")}: </span>
                                    <span className="additional-price-amount">
                                      +₩{Number(selectedVariant.additionalPrice).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                                {selectedVariant.stock === 0 && (
                                  <div className="gi-variant-stock out-of-stock">
                                    {t("품절")}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* 모바일에서 총 금액 표시 */}
                        {isMobile && (
                          <div className="mobile-total-price" style={{
                            marginBottom: '15px',
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: '700',
                            color: '#1a1a1a'
                          }}>
                            {/* @ts-ignore */}
                            <PriceDisplay price={finalPrice * quantity} />
                          </div>
                        )}
                        
                        <div className="purchase-container">
                          <div className="qty-plus-minus">
                            <QuantitySelector />
                          </div>
                          <div className="gi-single-cart">
                          <button 
                            className={`btn btn-primary gi-btn-1 ${isOutOfStock ? 'out-of-stock' : ''}`} 
                            onClick={handleAddToCart}
                            disabled={isOutOfStock}
                          >
                            {isOutOfStock ? t('품절') : t('장바구니 추가')}
                          </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            {/* Single product content End */}
            
            {/* Product Tab Start */}
            {visibleTabs.length > 0 && (
              <div className="gi-pro-tab gi-pro-tab-style1">
                <ul className="nav nav-tabs gi-pro-tab-nav" role="tablist">
                  {showDescriptionTab && (
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${currentActiveTab === 'description' ? 'active' : ''}`} 
                        onClick={(e) => {
                          e.preventDefault();
                          handleTabChange('description');
                        }}
                        href="#description"
                        role="tab"
                      >
                        {t('상품 설명')}
                      </a>
                    </li>
                  )}
                  {showReviewsTab && (
                    <li className="nav-item">
                      <a 
                        className={`nav-link ${currentActiveTab === 'reviews' ? 'active' : ''}`} 
                        onClick={(e) => {
                          e.preventDefault();
                          handleTabChange('reviews');
                        }}
                        href="#reviews" 
                        role="tab"
                      >
                        {t('리뷰')}
                      </a>
                    </li>
                  )}
                </ul>
                <div className="tab-content gi-pro-tab-content">
                  {showDescriptionTab && (
                    <div className={`tab-pane fade ${currentActiveTab === 'description' ? 'show active' : ''}`} id="description" role="tabpanel">
                      <div className="gi-description-block">
                        <div className="gi-description-detail">
                          {descriptionImages.length > 0 ? (
                            <div className="description-images">
                              {descriptionImages.map((imgUrl: string, index: number) => (
                                <div key={index} style={{marginBottom: "15px"}}>
                                  {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                                  <SafeImage 
                                    src={imgUrl} 
                                    alt={t('상품 상세 이미지') + ' ' + (index + 1)} 
                                    style={{width: "100%", height: "auto"}} 
                                    placeholderStyle={{
                                      width: "100%",
                                      paddingTop: "75%", // 4:3 비율
                                      backgroundColor: "#cccccc",
                                      marginBottom: "15px",
                                      position: "relative"
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : product?.description ? (
                            <div dangerouslySetInnerHTML={{ 
                              __html: product.description.replace(
                                /!\[(.*?)\]\((.*?)\)/g, 
                                '<img src="$2" alt="$1" style="width:100%; height:auto; margin:10px 0;" />'
                              ) 
                            }} />
                          ) : (
                            <p>{t('상품 설명이 없습니다.')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {showReviewsTab && (
                    <div className={`tab-pane fade ${currentActiveTab === 'reviews' ? 'show active' : ''}`} id="reviews" role="tabpanel">
                      <div className="gi-desc-reviews">
                        <div className="gi-reviews-wrapper">
                          <h4 className="gi-reviews-title">{t('고객 리뷰')}</h4>
                          {product?.recentReviews && product.recentReviews.length > 0 ? (
                            <div className="gi-review-box">
                              {product.recentReviews.map((review: any, index: number) => (
                                <div className="gi-review" key={index}>
                                  
                                  <div className="gi-review-content">
                                    <div className="gi-review-top">
                                      <div className="gi-review-name">{review.userName || t("사용자")}</div>
                                      <div className="gi-rating-star">
                                        <StarRating rating={review.rating || 0} />
                                      </div>
                                      <div className="gi-review-date">
                                        {new Date(review.createdAt).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <div className="gi-review-bottom">
                                      <div dangerouslySetInnerHTML={{ __html: review.content }} />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="gi-review-box">
                              <p>{t('아직 리뷰가 없습니다.')}</p>
                            </div>
                          )}
                          {product?.totalReviews && product.totalReviews > 0 && (
                            <div className="gi-review-stats">
                              <p>{t('총 리뷰 수')}: {product.totalReviews}{t('개')}</p>
                              <p>{t('평균 평점')}: <StarRating rating={product.averageRating || 0} /> ({product.averageRating ? product.averageRating.toFixed(1) : '0.0'})</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Product Tab End */}
          </Col>
          
          {/* Optional Sidebar */}
          {none !== "none" && (
            <Col lg={3} md={12} className={`gi-common-leftside ${order}`}>
              <div className="gi-sidebar-wrapper">
                <div className="gi-sidebar-block">
                  <div className="gi-sidebar-title">
                    <h3>{t("카테고리")}</h3>
                  </div>
                  <div className="gi-sidebar-content">
                    <ul className="gi-category-list">
                      <li><a href="/category/snacks">{t("간식")}</a></li>
                      <li><a href="/category/beverages">{t("음료")}</a></li>
                      <li><a href="/category/dairy">{t("유제품")}</a></li>
                      <li><a href="/category/fruits-vegetables">{t("과일 & 채소")}</a></li>
                    </ul>
                  </div>
                </div>
                {relatedProducts && Array.isArray(relatedProducts) && relatedProducts.length > 0 && (
                  <div className="gi-sidebar-block">
                    <div className="gi-sidebar-title">
                      <h3>{t('인기 상품')}</h3>
                    </div>
                    <div className="gi-sidebar-content">
                      <ul className="gi-popular-product">
                        {relatedProducts.slice(0, 3).map((relatedProduct: any, index: number) => (
                          <li key={index}>
                            <div className="gi-pro-image">
                              {/* @ts-ignore SafeImage 컴포넌트 prop 타입 오류 무시 */}
                              <SafeImage 
                                src={relatedProduct.image} 
                                alt={relatedProduct.title || t("관련 상품")} 
                                placeholderStyle={{ width: "100%", height: "100px", backgroundColor: "#cccccc" }}
                              />
                            </div>
                            <div className="gi-pro-content">
                              <h5>{t(relatedProduct.title || "상품명")}</h5>
                              <div className="gi-rating-star">
                                <StarRating rating={typeof relatedProduct.rating === 'number' ? relatedProduct.rating : 5} />
                              </div>
                              <span className="gi-price">
                                <span className="new-price">${typeof relatedProduct.newPrice === 'number' ? relatedProduct.newPrice.toFixed(2) : "0.00"}</span>
                                <span className="old-price">${typeof relatedProduct.oldPrice === 'number' ? relatedProduct.oldPrice.toFixed(2) : "0.00"}</span>
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </section>
  );
};

export default BasicProductDetailSkin;